import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import AttendancePaymentLinkInfo from '@components/attendance-payment-link-info/AttendancePaymentLinkInfo';
import { MEMBER_PHONE_NUMBER_MAX_LENGTH } from '@components/country-code/constants';
import { CountryData } from '@components/country-code/types';
import Label from '@components/label/Label';
import MonthCard from '@components/payment-collection/components/MonthCard';
import { FEATURE_FLAGS } from '@constants/feature';
import { duplicatePhoneNumberErrorMessage } from '@constants/generic';
import { invalidCharInNameRegex } from '@constants/regex';
import CustomTermTable from '@features/group-management/components/v1/custom-term/CustomTermTable';
import { PaymentCollectionType } from '@features/group-management/constants';
import { HIDE_APPLY_DISCOUNT_IN_CUSTOM_TERM } from '@features/settings/constants';
import { isValidPhoneNumber } from '@helpers/generic';
import ModalV2 from '@layouts/modal/Modal.v2';
import {
  AddMemberFormDataType,
  MemberListType,
  PaymentCollectionArg,
  TermsList,
  ValidationError
} from '@types';
import {
  getAmountErrorMessage,
  getNameErrorMessage,
  isMinLengthSatisfied,
  roundTo2,
  sanitizeNumberInput
} from '@utils/generic';

import AmountInput from '../amount-input/AmountInput';
import AmountInputTooltip from '../amount-input-tooltip/AmountInputTooltip';
import CountryCode from '../country-code/CountryCode';
import FeeConfigInput from '../fee-config-input/FeeConfigInput';
import Input from '../input/Input';
import Switch from '../switch/Switch';
import MemberDueDate from './MemberDueDate';
import MemberEndDate from './MemberEndDate';
import MemberStartDate from './MemberStartDate';

const getMonthDayLabel = (activationDate: string): string => {
  if (activationDate === 'first_day') return '1st day of the month';
  if (activationDate === 'last_day') return 'Last day of the month';
  if (activationDate && !Number.isNaN(Number(activationDate))) {
    const n = Number(activationDate);
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    const suffix = s[(v - 20) % 10] || s[v] || s[0];
    return `${n}${suffix} day of the month`;
  }
  return '1st day of the month';
};

interface MemberBasicInfoProps {
  error: ValidationError;
  setError: (error: ValidationError) => void;
  memberNameMaxLength: number;
  memberNameMinLength: number;
  isFeeCategoryEnabled: boolean;
  isImmediatePayment: boolean;
  isCustomTerm: boolean;
  groupMemberMaxAmount: string;
  groupMemberMinAmount: string;
  selectedmember?: MemberListType | null;
  paymentCollectionType: PaymentCollectionType | '';
  groupFrequency?: number;
  groupDueDate?: string;
  groupStartDate?: string;
  groupEndDate?: string;
  formData: AddMemberFormDataType;
  handleFormDataChange: (
    field: keyof AddMemberFormDataType,
    value: string
  ) => void;
  supported_phone_number_countries: any;
  setFormData: React.Dispatch<React.SetStateAction<AddMemberFormDataType>>;
  setIsCalendarVisible: (isCalendarVisible: boolean) => void;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
  sessionsPerCycle?: number;
  containerRef: React.RefObject<HTMLElement>;
  onFocused: () => void;
  defaultTermLabel?: string;
}

const MemberBasicInfo = ({
  error,
  memberNameMaxLength,
  memberNameMinLength,
  isFeeCategoryEnabled,
  isImmediatePayment,
  isCustomTerm,
  groupMemberMaxAmount,
  groupMemberMinAmount,
  selectedmember,
  paymentCollectionType,
  groupFrequency,
  groupDueDate,
  groupStartDate,
  groupEndDate,
  formData,
  setError,
  handleFormDataChange,
  supported_phone_number_countries,
  setFormData,
  setIsCalendarVisible,
  isAttendanceBasedFee = false,
  isSessionBased = false,
  sessionsPerCycle,
  containerRef,
  onFocused,
  defaultTermLabel
}: MemberBasicInfoProps) => {
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [tempActivationDate, setTempActivationDate] = useState<string>('');

  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const {
    name,
    primaryCountryCode,
    primaryNumber,
    altCountryCode,
    altNumber,
    amount,
    feeCategories,
    dueDate,
    startDate,
    endDate,
    totalFee
  } = formData;

  const formattedFeeCategories = feeCategories.map(({ label, value }) => ({
    category: label,
    amount: value
  }));

  const onMemberNameChange = (value: string) => {
    setError({ ...error, name: '' });
    handleFormDataChange('name', value);
  };

  const onMemberNameBlur = (value: string) => {
    if (!isMinLengthSatisfied(value))
      setError({
        ...error,
        name: getNameErrorMessage(memberNameMinLength)
      });
  };

  const handleSessionsChange = (value: string) => {
    const num = value ? Number(value) : undefined;
    if (num && num > 9999) return;
    handleFormDataChange('sessionsPerCycle', `${num ?? ''}`);
  };

  const getCountryId = (countryCode: string) =>
    (supported_phone_number_countries || []).find(
      (countryData: CountryData) => `${countryData.dial_code}` === countryCode
    )?.code;

  const onPrimaryCountryCodeChange = (value: string) => {
    const numberToValidate =
      formData.primaryNumber.length > MEMBER_PHONE_NUMBER_MAX_LENGTH
        ? formData.primaryNumber.slice(0, MEMBER_PHONE_NUMBER_MAX_LENGTH)
        : formData.primaryNumber;
    if (
      numberToValidate &&
      !isValidPhoneNumber(numberToValidate, getCountryId(value))
    ) {
      setError({ ...error, primaryNum: 'Invalid phone number' });
    } else {
      setError({ ...error, primaryNum: '' });
    }
    if (numberToValidate !== formData.primaryNumber) {
      handleFormDataChange('primaryNumber', numberToValidate);
    }
    handleFormDataChange('primaryCountryCode', value);
  };

  const onPrimaryNumberChange = (value: string) => {
    if (formData.primaryNumber === formData.altNumber) {
      setError({ ...error, altNum: '', primaryNum: '' });
    } else {
      setError({ ...error, primaryNum: '' });
    }
    if (value.length <= MEMBER_PHONE_NUMBER_MAX_LENGTH) {
      handleFormDataChange('primaryNumber', value);
    }
  };

  const onPrimaryNumberBlur = (value: string) => {
    if (!value) return;
    let errorMessage = '';
    if (!isValidPhoneNumber(value, getCountryId(formData.primaryCountryCode)))
      errorMessage = 'Invalid phone number';
    if (errorMessage !== '') setError({ ...error, primaryNum: errorMessage });
    if (
      value &&
      `${formData.primaryCountryCode}${value}` ===
        `${formData.altCountryCode}${formData.altNumber}`
    )
      setError({ ...error, altNum: duplicatePhoneNumberErrorMessage });
  };

  const onAltCountryCodeChange = (value: string) => {
    const numberToValidate =
      formData.altNumber.length > MEMBER_PHONE_NUMBER_MAX_LENGTH
        ? formData.altNumber.slice(0, MEMBER_PHONE_NUMBER_MAX_LENGTH)
        : formData.altNumber;
    if (
      numberToValidate &&
      !isValidPhoneNumber(numberToValidate, getCountryId(value))
    ) {
      setError({ ...error, altNum: 'Invalid phone number' });
    } else {
      setError({ ...error, altNum: '' });
    }
    if (numberToValidate !== formData.altNumber) {
      handleFormDataChange('altNumber', numberToValidate);
    }
    handleFormDataChange('altCountryCode', value);
  };

  const onAltNumberChange = (value: string) => {
    if (formData.altNumber === formData.primaryNumber) {
      setError({ ...error, primaryNum: '', altNum: '' });
    } else {
      setError({ ...error, altNum: '' });
    }
    if (value.length <= MEMBER_PHONE_NUMBER_MAX_LENGTH) {
      handleFormDataChange('altNumber', value);
    }
  };

  const onAltNumberBlur = (value: string) => {
    if (!value) return;
    let errorMessage = '';
    if (!isValidPhoneNumber(value, getCountryId(formData.altCountryCode)))
      errorMessage = 'Invalid phone number';
    else if (
      value &&
      `${formData.primaryCountryCode}${formData.primaryNumber}` ===
        `${formData.altCountryCode}${value}`
    )
      errorMessage = duplicatePhoneNumberErrorMessage;
    if (errorMessage !== '') setError({ ...error, altNum: errorMessage });
  };

  const onAmountChange = (value: string) => {
    setError({ ...error, amount: '' });
    const sanitizedAmount = sanitizeNumberInput(value, true);
    if (Number(sanitizedAmount) > Number(groupMemberMaxAmount)) {
      setError({
        ...error,
        amount: `Amount cannot be greater than ${groupMemberMaxAmount}`
      });
    }
    handleFormDataChange('amount', sanitizedAmount);
  };

  const onTotalFeeChange = (value: string) => {
    setError({ ...error, totalFee: '' });
    handleFormDataChange('totalFee', value);
    if (
      Number(value) !== 0 &&
      value !== '0' &&
      Number(value) > Number(groupMemberMaxAmount)
    ) {
      setError({
        ...error,
        totalFee: `Amount cannot be greater than ${groupMemberMaxAmount}`
      });
    }
  };

  const onAmountBlur = (value: string) => {
    if (
      value !== '0' &&
      Number(value) !== 0 &&
      Number(value) < Number(groupMemberMinAmount)
    ) {
      setError({
        ...error,
        amount: getAmountErrorMessage(Number(groupMemberMinAmount))
      });
    }
  };

  const onTotalFeeBlur = (value: string) => {
    if (Number(value) !== 0 && Number(value) < Number(groupMemberMinAmount)) {
      setError({
        ...error,
        totalFee: `Amount cannot be less than ${groupMemberMinAmount}`
      });
    }
  };

  const onCategoryAmountChange = (index: number, value: string) => {
    const updatedCategories = [...formData.feeCategories];
    updatedCategories[index].value = sanitizeNumberInput(value, true);
    updatedCategories[index].isManuallyUpdated = true;
    const totalAmount = roundTo2(
      updatedCategories.reduce(
        (sum, category) => sum + (Number(category.value) || 0),
        0
      )
    );
    setFormData({
      ...formData,
      amount: totalAmount.toString(),
      feeCategories: updatedCategories
    });
  };

  const onDueDateChange = (data: PaymentCollectionArg) => {
    setFormData({
      ...formData,
      dueDate: {
        day: data.date as string,
        interval: data.interval,
        paymentCollectionType: data.type
      }
    });
    setIsCalendarVisible(false);
  };

  const onTermsListChange = (termsList: TermsList[]) => {
    setFormData((prevFormData: AddMemberFormDataType) => ({
      ...prevFormData,
      termsList
    }));
  };

  const onStartDateChange = (date: string) => {
    handleFormDataChange('startDate', date);
  };

  const getEndOfTermDate = () => {
    if (!isCustomTerm || !formData.termsList || formData.termsList.length === 0)
      return undefined;

    const finalTerm = formData.termsList[formData.termsList.length - 1];
    return new Date(finalTerm.dueDate);
  };

  const onEndDateChange = (date: string) => {
    setError({ ...error, endDate: '' });
    handleFormDataChange('endDate', date);
    if (date && new Date(date) < new Date(startDate)) {
      setError({
        ...error,
        endDate: 'End date cannot be before start date'
      });
    }

    if (date && groupEndDate && new Date(date) > new Date(groupEndDate)) {
      setError({
        ...error,
        endDate: 'End date cannot be after group end date'
      });
    }
  };

  const handleOpenBillingModal = () => {
    setTempActivationDate(dueDate.day || 'first_day');
    setIsBillingModalOpen(true);
  };

  const handleBillingModalApply = () => {
    setFormData({
      ...formData,
      dueDate: {
        day: tempActivationDate,
        interval: '1',
        paymentCollectionType: PaymentCollectionType.MONTHLY
      }
    });
    setIsBillingModalOpen(false);
  };

  const handleBillingModalCancel = () => {
    setTempActivationDate('');
    setIsBillingModalOpen(false);
  };

  return (
    <div
      role="presentation"
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={onFocused}
      onClick={onFocused}
      onFocus={onFocused}
      className="rounded-xl bg-theme p-6 shadow-tableShadow"
    >
      <div className="grid w-full grid-cols-2 gap-4">
        <div className="gap-4">
          <Label label="Name" isRequired />
          <Input
            value={name}
            handleInputChange={onMemberNameChange}
            inputRegex={invalidCharInNameRegex}
            type="text"
            placeholder="Name"
            maxLength={memberNameMaxLength}
            minLength={memberNameMinLength}
            error={!!error.name}
            errorMessage={error.name}
            handleInputBlur={onMemberNameBlur}
          />
        </div>

        <div className="gap-4">
          <Label label="Primary Number" isRequired />
          <div className="flex gap-3">
            <CountryCode
              hasSelection
              countryCode={primaryCountryCode}
              setCountryCode={onPrimaryCountryCodeChange}
            />
            <Input
              className="w-full"
              value={primaryNumber}
              handleInputChange={onPrimaryNumberChange}
              placeholder="Enter phone number"
              type="phone"
              error={!!error.primaryNum}
              errorMessage={error.primaryNum}
              handleInputBlur={onPrimaryNumberBlur}
            />
          </div>
        </div>

        <div className="gap-4">
          <Label label="Alternate Number" />
          <div className="flex gap-3">
            <CountryCode
              hasSelection
              countryCode={altCountryCode}
              setCountryCode={onAltCountryCodeChange}
            />
            <Input
              className="w-full"
              value={altNumber}
              handleInputChange={onAltNumberChange}
              placeholder="Enter phone number"
              type="phone"
              error={!!error.altNum}
              errorMessage={error.altNum}
              handleInputBlur={onAltNumberBlur}
              errorStyle={
                error.altNum === duplicatePhoneNumberErrorMessage
                  ? '!top-15'
                  : ''
              }
            />
          </div>
        </div>
        {isAttendanceBasedFee ? (
          <>
            <div>
              <div className="flex flex-col">
                <Label label="Rate per class" isRequired />
                <AmountInput
                  value={amount}
                  placeholder="Enter rate per class"
                  onAmountChange={onAmountChange}
                  handleInputBlur={onAmountBlur}
                  error={!!error.amount}
                  maxAmount={groupMemberMaxAmount}
                  allowDecimals
                  allowZero
                />
                <AmountInputTooltip
                  maxAmount={groupMemberMaxAmount}
                  minAmount={groupMemberMinAmount}
                  type="hasSubtext"
                />
              </div>
            </div>

            {isSessionBased ? (
              <div>
                <div className="flex flex-col">
                  <Label label="Billing Period" isRequired />
                  <Input
                    value={sessionsPerCycle ? String(sessionsPerCycle) : '0'}
                    placeholder="Enter billing period"
                    className="w-full"
                    suffixText={
                      sessionsPerCycle && sessionsPerCycle > 1
                        ? 'Classes'
                        : 'Class'
                    }
                    type="number"
                    onChange={(e) => handleSessionsChange(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <>
                <div>
                  <div className="flex flex-col">
                    <Label label="Billing Period" isRequired />
                    <Input
                      leftIconName="calendar"
                      placeholder="Select billing period"
                      value={getMonthDayLabel(dueDate.day || 'first_day')}
                      onClick={handleOpenBillingModal}
                      className="w-full"
                      readOnly
                    />
                  </div>
                </div>

                {isBillingModalOpen && (
                  <ModalV2
                    title="Billing period"
                    onClose={handleBillingModalCancel}
                    primaryButtonLabel="Apply"
                    primaryButtonClick={handleBillingModalApply}
                    secondaryButtonLabel="Cancel"
                    secondaryButtonClick={handleBillingModalCancel}
                  >
                    <MonthCard
                      handleActivationDate={(value: string | Date) =>
                        setTempActivationDate(String(value))
                      }
                      selectedDate={tempActivationDate}
                      type={PaymentCollectionType.MONTHLY}
                      handleIntervalFrequency={() => {}}
                    />
                    <AttendancePaymentLinkInfo />
                  </ModalV2>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <div>
              <div className="flex flex-col">
                <div className="mb-4 text-sm font-semibold text-cofeeNeutral10">
                  Amount
                </div>
                <AmountInput
                  value={amount}
                  placeholder="Enter Amount"
                  onAmountChange={onAmountChange}
                  handleInputBlur={onAmountBlur}
                  error={isCustomTerm ? false : !!error.amount}
                  disabled={isFeeCategoryEnabled || isCustomTerm}
                  allowDecimals
                  allowZero
                />
                {!isCustomTerm && (
                  <AmountInputTooltip
                    maxAmount={groupMemberMaxAmount}
                    minAmount={groupMemberMinAmount}
                    type="hasSubtext"
                  />
                )}
              </div>
            </div>

            {!isImmediatePayment && (
              <MemberDueDate
                dueDate={dueDate}
                memberPaymentCollectionType={
                  selectedmember?.paymentCollectionType as PaymentCollectionType
                }
                memberInterval={selectedmember?.interval as string}
                memberDueDate={selectedmember?.dueDate as string}
                paymentCollectionType={paymentCollectionType}
                groupFrequency={groupFrequency as number}
                groupDueDate={groupDueDate}
                onDueDateChange={onDueDateChange}
                isCustomTerm={isCustomTerm}
              />
            )}
            {/* Start date */}
            {paymentCollectionType !== PaymentCollectionType.ONCE && (
              <MemberStartDate
                startDate={startDate || groupStartDate || ''}
                onStartDateChange={onStartDateChange}
                maxLimitDate={getEndOfTermDate()}
                isDisabled={isCustomTerm}
              />
            )}

            {paymentCollectionType !== PaymentCollectionType.ONCE &&
              orgFeatureFlags?.includes(FEATURE_FLAGS.MEMBER_END_DATE) && (
                <MemberEndDate
                  endDate={endDate}
                  onEndDateChange={onEndDateChange}
                  isDisabled={isCustomTerm}
                  error={!!error.endDate}
                  errorMessage={error.endDate}
                />
              )}

            {orgFeatureFlags &&
              orgFeatureFlags.includes(FEATURE_FLAGS.LEAD_SQUARED) && (
                <div>
                  <div className="flex flex-col">
                    <div className="mb-4 text-sm font-semibold text-cofeeNeutral10">
                      Course fee
                    </div>
                    <AmountInput
                      value={totalFee}
                      placeholder="Enter course fee"
                      onAmountChange={onTotalFeeChange}
                      handleInputBlur={onTotalFeeBlur}
                      error={!!error.totalFee}
                      errorMessage={error.totalFee}
                    />
                  </div>
                </div>
              )}

            {isFeeCategoryEnabled && !isCustomTerm && (
              <div className="col-span-2">
                <FeeConfigInput
                  feeCategories={formattedFeeCategories}
                  onCategoryAmountChange={({ index, value }) => {
                    onCategoryAmountChange(index, value);
                  }}
                />
              </div>
            )}

            {isCustomTerm && formData.termsList?.length > 0 && (
              <div className="col-span-2">
                <CustomTermTable
                  termsList={formData.termsList}
                  setTermsList={onTermsListChange}
                  isFeeCategoryEnabled={isFeeCategoryEnabled}
                  feeCategories={feeCategories}
                  onAmountChange={(totalAmount) =>
                    handleFormDataChange('amount', totalAmount)
                  }
                  isMember
                  memberFlow="add"
                  setError={setError}
                  error={error}
                  className="!border-none !p-0"
                  defaultTermLabel={defaultTermLabel}
                />
              </div>
            )}

            {isCustomTerm && !HIDE_APPLY_DISCOUNT_IN_CUSTOM_TERM && (
              <div className="col-span-2 flex flex-col gap-4 border-t border-cofeeGreyBlue5 pt-4">
                <div>
                  <Switch
                    value={formData.discount?.isEnabled || false}
                    handleClick={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        discount: {
                          isEnabled: val,
                          label: val ? prev.discount?.label || '' : '',
                          value: val ? prev.discount?.value || '' : ''
                        }
                      }))
                    }
                    label="Apply discount"
                    labelClassName="!text-sm !font-semibold !text-cofeeNeutral10"
                    switchPosition="left"
                    testId="apply-discount"
                    className="!justify-start"
                  />
                  <p className="ml-8 mt-1 text-xs text-cofeeNeutral7">
                    Discount on total. Payers see original struck through.
                  </p>
                </div>

                {formData.discount?.isEnabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="mb-2 text-sm font-medium text-cofeeNeutral10">
                        Discount label
                      </div>
                      <Input
                        type="text"
                        value={formData.discount?.label || ''}
                        placeholder="Eg: Scholarship"
                        className="w-full"
                        inputSize="medium"
                        handleInputChange={(val: string) =>
                          setFormData((prev) => ({
                            ...prev,
                            discount: {
                              ...prev.discount!,
                              label: val
                            }
                          }))
                        }
                      />
                    </div>
                    <div>
                      <div className="mb-2 text-sm font-medium text-cofeeNeutral10">
                        Amount
                      </div>
                      <AmountInput
                        name="discountAmount"
                        value={formData.discount?.value || ''}
                        placeholder="1000"
                        className="w-full"
                        inputSize="medium"
                        onAmountChange={(val: string) =>
                          setFormData((prev) => ({
                            ...prev,
                            discount: {
                              ...prev.discount!,
                              value: val
                            }
                          }))
                        }
                        maxAmount={formData.amount}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MemberBasicInfo;
